import { EllipsisVertical, Gift, Info, Pencil, Search } from 'lucide-react'
import { type MouseEvent, useState } from 'react'
import { useNavigate } from 'react-router'

import { fromCents } from '@ankor-io/common/domain/pay/currency'
import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { PaymentMethod, type TransactionPayload, TransactionType } from '@ankor-io/common/types/transaction/transaction'
import { formatCurrency } from '@ankor-io/common/util/currencies'
import { formatDateShort } from '@ankor-io/common/util/dates'
import { getExpensePaymentStatus } from '@ankor-io/common/util/enum-to-string/expense.payment.status'
import { getPaymentMethodText } from '@ankor-io/common/util/enum-to-string/payment.method'
import { getStatusColour, getStatusText } from '@ankor-io/common/util/enum-to-string/transaction'
import { Badge } from '@ankor-io/ui-components/src/Badge'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@ankor-io/ui-components/src/Dropdown'
import { EmptyState } from '@ankor-io/ui-components/src/EmptyState'
import { Heading, Subheading } from '@ankor-io/ui-components/src/Heading'
import { Loading } from '@ankor-io/ui-components/src/Loading'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ankor-io/ui-components/src/Table'
import { Tooltip } from '@ankor-io/ui-components/src/Tooltip'

import { useListTransactionsQuery } from '@/api/queries'
import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { useModuleTheme } from '@/providers/moduleTheme'
import { useUserContext } from '@/providers/user/useUserContext'
import { formatUsername } from '@/utils/formatUsername'
import {
  canEditTransferPaymentMethodExpense,
  canUserEditOpsTransaction,
  canUserPerformOpsTransactionActions,
} from '@/utils/transactionPermissions'
import { CategoryLabel } from '@/views/components/CategoryLabel'
import { SortableTableHeader } from '@/views/components/SortableTableHeader'
import {
  DEFAULT_TRANSACTION_TABLE_SORT,
  type TransactionSortBy,
  type TransactionTableSort,
  getDefaultSortOrder,
  getNextTransactionSort,
} from '@/views/components/transactionTableSort'

const TRANSACTIONS_PER_PAGE = 10

const DashboardTransactions = () => {
  const navigate = useNavigate()
  const { moduleTheme } = useModuleTheme()
  const { user, activeOrganizationRole } = useUserContext()
  const [tableSort, setTableSort] = useState<TransactionTableSort>(DEFAULT_TRANSACTION_TABLE_SORT)
  const { sortBy, sortOrder } = tableSort
  const { data, isPending } = useListTransactionsQuery({ sortBy, sortOrder }, TRANSACTIONS_PER_PAGE)

  // Flatten all pages into a single array
  const transactions = data?.pages.flat() ?? []

  const showLoader = useMinimumDisplayTime(isPending, 1000)

  const handleEditTransaction = (transaction: TransactionPayload, e: MouseEvent) => {
    e.stopPropagation()
    navigate(`/ops/transactions/${transaction.id}/edit`)
  }

  const handleSortChange = (sortKey: TransactionSortBy) => {
    setTableSort((current) => getNextTransactionSort(current, sortKey))
  }

  return (
    <div className="w-full rounded-lg shadow-sm border divide-y border-gray-200 dark:border-gray-700 divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
      <Heading
        level={4}
        className="p-4"
      >
        Expenses
      </Heading>

      <div className="p-4">
        <div className="flex items-center gap-x-4 mb-4">
          <Subheading>Recent expenses</Subheading>
          <Button
            color={moduleTheme.button}
            onClick={() => navigate('/ops/transactions')}
          >
            View all
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table className="w-[calc(100vw-4.2rem)] lg:w-[calc(100vw-23.1rem)] xl:w-auto">
            <TableHead>
              <TableRow>
                <SortableTableHeader
                  label="Date / Spender"
                  sortKey="date"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                />
                <SortableTableHeader
                  label="Expense Status"
                  sortKey="status"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                />
                <SortableTableHeader
                  label="Merchant"
                  sortKey="vendor"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                />
                <SortableTableHeader
                  label="Category"
                  sortKey="categoryName"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                />
                <TableHeader>Payment Method</TableHeader>
                <TableHeader>
                  <div className="inline-flex items-center gap-1">
                    <span>Payment Status</span>
                    <span
                      aria-label="About Payment Status"
                      data-tooltip-id="ops-dashboard-payment-status-tooltip"
                      className="cursor-help text-gray-400 dark:text-gray-500"
                    >
                      <Info
                        className="size-4"
                        aria-hidden
                      />
                    </span>
                    <Tooltip
                      id="ops-dashboard-payment-status-tooltip"
                      place="top"
                      className="z-20"
                    >
                      Shows the status of the payment on the payment rail (e.g. Unpaid, Pending, Success, Sent, Paid).
                    </Tooltip>
                  </div>
                </TableHeader>
                <SortableTableHeader
                  label="Total"
                  sortKey="amountCents"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                  className="text-right"
                />
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {showLoader ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-12"
                  >
                    <div className="flex items-center justify-center">
                      <Loading />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => {
                  const paymentStatus = getExpensePaymentStatus(transaction, AwxConnectedAccountType.OPERATIONS)
                  return (
                    <TableRow
                      className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                      key={transaction.id}
                      onClick={() => navigate(`/ops/transactions/${transaction.id}/details`)}
                    >
                      {/* Date */}
                      <TableCell>
                        <div className="flex flex-col">
                          <div>{formatDateShort(transaction.date)}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {formatUsername(transaction.transactedBy)}
                          </div>
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Badge color={getStatusColour(transaction.status)}>{getStatusText(transaction.status)}</Badge>
                      </TableCell>

                      {/* Merchant */}
                      <TableCell className="max-w-48 truncate">
                        <span className="block truncate">{transaction.vendor}</span>
                      </TableCell>

                      {/* Category */}
                      <TableCell>{transaction.category && <CategoryLabel category={transaction.category} />}</TableCell>

                      {/* Payment Method */}
                      <TableCell>
                        <div
                          className={`${transaction.type === TransactionType.GRATUITY ? 'flex items-center gap-x-2' : ''}`}
                        >
                          {transaction.paymentMethod === PaymentMethod.BOAT_CASH && transaction.cashFloat?.name
                            ? `Boat Cash > ${transaction.cashFloat.name}`
                            : getPaymentMethodText(transaction.paymentMethod)}
                          {transaction.type === TransactionType.GRATUITY && (
                            <>
                              <Gift
                                className="size-4 shrink-0 relative"
                                data-tooltip-id={`${transaction.id}-gift-tooltip`}
                              />

                              <Tooltip
                                place="right"
                                id={`${transaction.id}-gift-tooltip`}
                                className="z-20"
                              >
                                Gratuity - Amount given by client to crew
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </TableCell>

                      {/* Payment Status */}
                      <TableCell>
                        {paymentStatus ? (
                          <Badge color={paymentStatus.colour}>{paymentStatus.text}</Badge>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">-</span>
                        )}
                      </TableCell>

                      {/* Total */}
                      <TableCell className="text-sm text-right text-gray-500 dark:text-gray-400">
                        {formatCurrency(fromCents(transaction.amountCents), transaction.currencyCode)}
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        {/* Show actions if user can perform any actions on this transaction */}
                        {canUserPerformOpsTransactionActions(transaction, user, activeOrganizationRole) &&
                          canUserEditOpsTransaction(transaction, user, activeOrganizationRole) &&
                          canEditTransferPaymentMethodExpense(transaction, activeOrganizationRole) && (
                            <div
                              className="relative z-10 pointer-events-auto"
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                              <Dropdown>
                                <DropdownButton plain>
                                  <EllipsisVertical data-slot="icon" />
                                </DropdownButton>
                                <DropdownMenu anchor="bottom end">
                                  <DropdownItem onClick={(e: MouseEvent) => handleEditTransaction(transaction, e)}>
                                    <Pencil data-slot="icon" />
                                    Edit
                                  </DropdownItem>
                                </DropdownMenu>
                              </Dropdown>
                            </div>
                          )}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>

        {!showLoader && transactions.length === 0 && (
          <EmptyState
            buttonColor={moduleTheme.button}
            heading="No expenses yet"
            subheading="Get started by making your first purchase. It will appear here once processed."
            icon={<Search className="mx-auto size-12 text-gray-400 dark:text-gray-500" />}
          />
        )}
      </div>
    </div>
  )
}

export default DashboardTransactions
